import React, { useState, useEffect, useRef, useMemo } from 'react';
import { X, Save, Grid, Trash2, Thermometer, Fan } from 'lucide-react';
import { LocationData, Floor, CellType, GridCell } from '../types';
import { FLOOR_DESIGN_BASE_COST, FLOOR_DESIGN_RACK_UNIT_COST, FLOOR_DESIGN_COOLING_UNIT_COST, SERVER_TYPES, CHILLER_TYPES, POWER_GRIDS } from '../constants';
import { calculateLocationGrossPower } from '../utils/powerCalculations';
import EditorRow from './EditorRow';

interface FloorDesignerProps {
  currentLocation: string;
  locations: Record<string, LocationData>;
  setLocations: React.Dispatch<React.SetStateAction<Record<string, LocationData>>>;
  setShowFloorDesigner: (show: boolean) => void;
  formatCurrency: (amount: number, decimals?: number) => string;
  money: number;
  setMoney: React.Dispatch<React.SetStateAction<number>>;
  triggerNotification: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void;
  editMode?: boolean;
  editFloorId?: string;
  initialTemplate?: 'full' | 'half';
  assignRoomId?: string;
}

const FloorDesigner: React.FC<FloorDesignerProps> = ({
  currentLocation,
  locations,
  setLocations,
  setShowFloorDesigner,
  formatCurrency,
  money,
  setMoney,
  triggerNotification,
  editMode = false,
  editFloorId,
  initialTemplate,
  assignRoomId
}) => {
  // Fixed grid size (1:1 cells): 18×8 room footprint
  const GRID_ROWS = 8;
  const GRID_COLS = 18;

  // Floor name/id
  const [floorName, setFloorName] = useState(`Floor ${Object.keys(locations[currentLocation]?.floors || {}).length + 1}`);

  // Initial empty grid
  const createEmptyGrid = () => {
    const grid: GridCell[][] = [];
    for (let r = 0; r < GRID_ROWS; r++) {
      const row: GridCell[] = [];
      for (let c = 0; c < GRID_COLS; c++) {
        row.push({ type: 'empty' });
      }
      grid.push(row);
    }
    return grid;
  };

  // State for the design grid
  const [grid, setGrid] = useState<GridCell[][]>(createEmptyGrid());
  const [originalGrid, setOriginalGrid] = useState<GridCell[][] | null>(null);

  // Selected tool
  const [selectedTool, setSelectedTool] = useState<CellType>('rack');

  // Cost estimate
  const [totalCost, setTotalCost] = useState(0);

  // Displayed cost: force 0 when in edit mode and grid unchanged (rename-only)
  const displayCost = useMemo(() => {
    if (editMode && originalGrid && gridsEqual(grid, originalGrid)) return 0;
    return totalCost;
  }, [editMode, originalGrid, grid, totalCost]);

  // In browser, window.setTimeout returns a number; use number to avoid Node Timeout type mismatch
  const costDebounceRef = useRef<number | null>(null);
  const scheduleCostRecalc = (designGrid: GridCell[][]) => {
    if (costDebounceRef.current) window.clearTimeout(costDebounceRef.current);
    costDebounceRef.current = window.setTimeout(() => {
      setTotalCost(calculateCost(designGrid));
    }, 200);
  };

  // Ensure debounce timer is cleared if component unmounts
  useEffect(() => {
    return () => {
      if (costDebounceRef.current) {
        window.clearTimeout(costDebounceRef.current);
      }
    };
  }, []);

  // Calculate cost based on the grid design
  const calculateCost = (designGrid: GridCell[][]) => {
    // Single-pass counting to avoid repeated flattening and filtering
    let rackCount = 0;
    let coolingCount = 0;
    for (const row of designGrid) {
      for (const cell of row) {
        if (cell.type === 'rack') rackCount++;
        else if (cell.type === 'cooling') coolingCount++;
      }
    }

    const rackUnit = FLOOR_DESIGN_RACK_UNIT_COST;
    const coolingUnit = FLOOR_DESIGN_COOLING_UNIT_COST;

    if (editMode && originalGrid) {
      let oldRackCount = 0;
      let oldCoolingCount = 0;
      for (const row of originalGrid) {
        for (const cell of row) {
          if (cell.type === 'rack') oldRackCount++;
          else if (cell.type === 'cooling') oldCoolingCount++;
        }
      }
      const deltaRacks = Math.max(0, rackCount - oldRackCount);
      const deltaCooling = Math.max(0, coolingCount - oldCoolingCount);
      return deltaRacks * rackUnit + deltaCooling * coolingUnit; // additions only
    }

    const baseFloorCost = FLOOR_DESIGN_BASE_COST; // Base cost for new floor construction
    return baseFloorCost + rackCount * rackUnit + coolingCount * coolingUnit;
  };

  // Helper: compare two design grids for equality (type-only)
  function gridsEqual(a: GridCell[][], b: GridCell[][]) {
    if (!a || !b) return false;
    if (a.length !== b.length) return false;
    for (let r = 0; r < a.length; r++) {
      const rowA = a[r];
      const rowB = b[r];
      if (!rowB || rowA.length !== rowB.length) return false;
      for (let c = 0; c < rowA.length; c++) {
        if ((rowA[c]?.type || 'empty') !== (rowB[c]?.type || 'empty')) return false;
      }
    }
    return true;
  };

  // Update grid cell
  const updateCell = (row: number, col: number) => {
    const current = grid[row][col];
    if (current.locked) {
      triggerNotification('This rack is occupied and cannot be moved or removed.', 'warning');
      return;
    }
    if (current.type === selectedTool) {
      // No change needed
      return;
    }
    // Functional immutable update to avoid stale state and unintended mutations
    setGrid((prev) => {
      const next = prev.map((r, rIdx) => {
        if (rIdx !== row) return r;
        const newRow = r.slice();
        newRow[col] = { type: selectedTool };
        return newRow;
      });
      // Update cost estimate (debounced) with the freshly computed grid
      scheduleCostRecalc(next);
      return next;
    });
  };

  // Clear design
  const clearDesign = () => {
    setGrid(createEmptyGrid());
    setTotalCost(0);
    triggerNotification('Floor design cleared', 'info');
  };

  // 18×8 Full Room template: walkway, hot, rack, cold, walkway, rack, hot, walkway
  const applyFullRoom18x8 = () => {
    const rows = GRID_ROWS;
    const cols = GRID_COLS;
    const newGrid: GridCell[][] = Array.from({ length: rows }, () => Array.from({ length: cols }, () => ({ type: 'empty' as CellType })));

    // Fill a row for the inner band only (leave side columns 0 and cols-1 as walkways)
    const setRow = (r: number, type: CellType, start = 1, end = cols - 1) => {
      for (let c = start; c < end; c++) newGrid[r][c] = { type };
    };

    // Walkway on top row
    setRow(0, 'empty', 0, cols);

    // Core bands (inner columns only)
    setRow(1, 'hot-aisle');
    setRow(2, 'rack');
    setRow(3, 'cold-aisle');
    setRow(4, 'empty'); // middle walkway
    setRow(5, 'rack'); // row 5 becomes racks
    setRow(6, 'hot-aisle'); // row 6 becomes hot aisle
    setRow(7, 'empty', 1, cols - 1); // bottom walkway

    // Cooling units at four corners on walkway rows
    newGrid[0][0] = { type: 'cooling' };
    newGrid[0][cols - 1] = { type: 'cooling' };
    newGrid[7][0] = { type: 'cooling' };
    newGrid[7][cols - 1] = { type: 'cooling' };

    setGrid(newGrid);
    setTotalCost(calculateCost(newGrid));
    triggerNotification('Applied 18×8 Full Room template (32 racks).', 'success');
  };

  // 18×8 Half Room template: walkway, hot, rack, cold, walkway, walkway, walkway, walkway (single rack row)
  const applyHalfRoom18x8 = () => {
    const rows = GRID_ROWS;
    const cols = GRID_COLS;
    const newGrid: GridCell[][] = Array.from({ length: rows }, () => Array.from({ length: cols }, () => ({ type: 'empty' as CellType })));

    const setRow = (r: number, type: CellType, start = 1, end = cols - 1) => {
      for (let c = start; c < end; c++) newGrid[r][c] = { type };
    };

    // Walkways remain empty on rows 0 and 7
    setRow(0, 'empty', 0, cols);
    setRow(7, 'empty', 0, cols);

    // Core bands (inner columns only)
    setRow(1, 'hot-aisle');
    setRow(2, 'rack');
    setRow(3, 'cold-aisle');
    setRow(4, 'empty'); // middle walkway
    setRow(5, 'empty'); // removed cold at row 5 -> walkway
    // Rows 6 and 7 remain walkway/empty service space

    // Cooling units at four corners on walkway rows
    newGrid[0][0] = { type: 'cooling' };
    newGrid[0][cols - 1] = { type: 'cooling' };
    newGrid[7][0] = { type: 'cooling' };
    newGrid[7][cols - 1] = { type: 'cooling' };

    setGrid(newGrid);
    setTotalCost(calculateCost(newGrid));
    triggerNotification('Applied 18×8 Half Room template (16 racks).', 'success');
  };

  // Auto-apply template when creating a new floor via Site Layout
  useEffect(() => {
    if (editMode) return; // do not auto-apply in edit mode
    if (!initialTemplate) return;
    if (initialTemplate === 'full') applyFullRoom18x8();
    else applyHalfRoom18x8();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTemplate, editMode]);

  // Prefill grid and name in edit mode (normalize to fixed 18×8)
  useEffect(() => {
    if (!editMode || !editFloorId) return;
    const loc = locations[currentLocation];
    const floor = loc?.floors?.[editFloorId];
    if (!floor) return;

    setFloorName(floor.name);

    const makeNormalized = (src: GridCell[][]): GridCell[][] => {
      const out: GridCell[][] = Array.from({ length: GRID_ROWS }, (_, r) => {
        const row = src[r] || [];
        return Array.from({ length: GRID_COLS }, (_, c) => {
          const cell = row[c];
          const base: GridCell = cell ? { type: cell.type as CellType } : { type: 'empty' as CellType };
          return base;
        });
      });
      // Lock occupied racks that are within bounds
      (floor.racks || []).forEach(r => {
        if (typeof r.x === 'number' && typeof r.z === 'number' && r.z < GRID_ROWS && r.x < GRID_COLS) {
          if (out[r.z][r.x].type === 'rack') {
            out[r.z][r.x] = { ...out[r.z][r.x], locked: (r.servers?.length || 0) > 0 };
          }
        }
      });
      return out;
    };

    let normalized: GridCell[][] = createEmptyGrid();
    let cropped = false;

    if (floor.designGrid && floor.designGrid.length > 0) {
      cropped = floor.designGrid.length > GRID_ROWS || Math.max(...floor.designGrid.map(r => r.length)) > GRID_COLS;
      normalized = makeNormalized(floor.designGrid);
    } else {
      // Build from racks within bounds
      const empty = createEmptyGrid();
      let anyOOB = false;
      (floor.racks || []).forEach(r => {
        const x = typeof r.x === 'number' ? (r.x as number) : -1;
        const z = typeof r.z === 'number' ? (r.z as number) : -1;
        if (x >= 0 && z >= 0 && z < GRID_ROWS && x < GRID_COLS) {
          empty[z][x] = { type: 'rack', locked: (r.servers?.length || 0) > 0 };
        } else {
          anyOOB = true;
        }
      });
      normalized = empty;
      cropped = anyOOB;
    }

    if (cropped) {
      triggerNotification('Design was normalized to 18×8. Some rows/columns or racks outside bounds were cropped.', 'info');
    }

    setGrid(normalized);
    setOriginalGrid(normalized.map(row => row.map(cell => ({ ...cell }))));
    setTotalCost(calculateCost(normalized));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMode, editFloorId, currentLocation]);

  // Save the floor design
  const saveDesign = () => {
    if (money < displayCost) {
      triggerNotification(`Not enough money! Need ${formatCurrency(displayCost)}`, 'error');
      return;
    }

    if (editMode && editFloorId) {
      // Editing an existing floor: preserve ID, update in place
      const loc = locations[currentLocation];
      const existing = loc?.floors?.[editFloorId];
      if (!existing) {
        triggerNotification('Cannot edit: floor not found.', 'error');
        return;
      }

      // Zero-cost rename-only path: if grid unchanged, only update the name
      if (originalGrid && gridsEqual(grid, originalGrid) && existing.name !== floorName) {
        setLocations(prev => {
          const locationData = { ...prev[currentLocation] } as any;
          const floors = { ...(locationData.floors || {}) } as Record<string, Floor>;
          const updated = { ...floors[editFloorId], name: floorName } as Floor;
          floors[editFloorId] = updated;

          // Also reflect the name change in any site layout room assigned to this floor
          if (locationData.rooms) {
            const roomsCopy = { ...(locationData.rooms as any) } as Record<string, any>;
            let changed = false;
            Object.keys(roomsCopy).forEach(rid => {
              const room = roomsCopy[rid];
              if (room && room.floorId === editFloorId && room.name !== floorName) {
                roomsCopy[rid] = { ...room, name: floorName };
                changed = true;
              }
            });
            if (changed) locationData.rooms = roomsCopy;
          }

          return { ...prev, [currentLocation]: { ...locationData, floors } };
        });
        setShowFloorDesigner(false);
        triggerNotification(`Floor renamed to "${floorName}".`, 'success');
        return;
      }

      // Build maps for existing rack positions
      const existingByPos = new Map<string, typeof existing.racks[number]>();
      let droppedWithServers = 0;
      (existing.racks || []).forEach(r => {
        if (typeof r.x === 'number' && typeof r.z === 'number') {
          if (r.z < GRID_ROWS && r.x < GRID_COLS) {
            existingByPos.set(`${r.z}-${r.x}`, r);
          } else if ((r.servers?.length || 0) > 0) {
            droppedWithServers++;
          }
        }
      });

      // Validate removals: block if trying to remove occupied racks
      const newRackPositions = new Set<string>();
      grid.forEach((row, rowIndex) => {
        row.forEach((cell, colIndex) => {
          if (cell.type === 'rack') newRackPositions.add(`${rowIndex}-${colIndex}`);
        });
      });
      let invalidRemoval = false;
      existingByPos.forEach((rack, pos) => {
        if (!newRackPositions.has(pos) && (rack.servers?.length || 0) > 0) {
          invalidRemoval = true;
        }
      });
      if (invalidRemoval) {
        triggerNotification('Cannot remove a rack that has installed servers. Remove servers first.', 'error');
        return;
      }

      // Build new rack array preserving IDs where positions match
      const newRacks: typeof existing.racks = [];
      grid.forEach((row, rowIndex) => {
        row.forEach((cell, colIndex) => {
          if (cell.type === 'rack') {
            const key = `${rowIndex}-${colIndex}`;
            const prev = existingByPos.get(key);
            if (prev) {
              newRacks.push(prev); // keep existing rack (with servers etc.)
            } else {
              newRacks.push({
                id: Date.now() + Math.floor(Math.random() * 1000),
                type: null,
                x: colIndex,
                z: rowIndex,
                servers: [],
                maxCapacity: 48,
                floorQuality: existing.floorQualityId || 'basic',
                effectiveCooling: 100,
                powerDraw: 0,
                coolingNeeded: 0
              });
            }
          }
        });
      });

      // Commit update
      setLocations(prev => {
        const locationData = { ...prev[currentLocation] };
        const updatedFloor: Floor = {
          ...existing,
          id: existing.id, // preserve
          name: floorName,
          racks: newRacks,
          designGrid: grid
        };
        locationData.floors = { ...(locationData.floors || {}) };
        locationData.floors[editFloorId] = updatedFloor;

        // Also reflect the name change in any site layout room assigned to this floor
        if (locationData.rooms) {
          const roomsCopy = { ...(locationData.rooms as any) } as Record<string, any>;
          let changed = false;
          Object.keys(roomsCopy).forEach(rid => {
            const room = roomsCopy[rid];
            if (room && room.floorId === editFloorId && room.name !== floorName) {
              roomsCopy[rid] = { ...room, name: floorName };
              changed = true;
            }
          });
          if (changed) (locationData as any).rooms = roomsCopy;
        }

        // Recompute floor-level usage for the updated floor
        let floorServerPower = 0;
        let floorServerCooling = 0;
        updatedFloor.racks.forEach(rack => {
          (rack.servers || []).forEach(server => {
            const st = SERVER_TYPES.find(s => s.id === server.type);
            floorServerPower += st ? st.powerDraw : (server.powerDraw || 0);
            floorServerCooling += st ? st.coolingNeeded : (server.coolingNeeded || 0);
          });
        });
        updatedFloor.usedPower = floorServerPower;
        updatedFloor.usedCooling = floorServerCooling;

        // Recompute location-wide usage across all floors
        let totalServerPower = 0;
        let totalServerCooling = 0;
        Object.values(locationData.floors || {}).forEach(floor => {
          (floor.racks || []).forEach(rack => {
            (rack.servers || []).forEach(server => {
              const st = SERVER_TYPES.find(s => s.id === server.type);
              totalServerPower += st ? st.powerDraw : (server.powerDraw || 0);
              totalServerCooling += st ? st.coolingNeeded : (server.coolingNeeded || 0);
            });
          });
        });

        // Chillers: power consumption and cooling capacity
        let chillersPower = 0;
        let totalCooling = 0;
        (locationData.chillers || []).forEach(ch => {
          const type = CHILLER_TYPES.find(t => t.id === ch.typeId);
          const level = type?.levels.find(l => l.level === ch.level);
          if (level) {
            chillersPower += level.powerConsumptionKW;
            totalCooling += level.coolingOutputKW * level.efficiency;
          }
        });

        const usedPower = totalServerPower + chillersPower;
        const usedCooling = totalServerCooling;

        // Recalculate gross and available power
        const grossPower = calculateLocationGrossPower(locationData, POWER_GRIDS);
        locationData.grossPowerKW = grossPower;
        locationData.usedPower = usedPower;
        locationData.availablePowerKW = grossPower - usedPower;

        // Update cooling capacity/usage on location
        locationData.totalCoolingCapacityKW = totalCooling;
        locationData.usedCoolingCapacityKW = usedCooling;
        locationData.coolingCapacity = totalCooling;
        locationData.usedCooling = usedCooling;

        return { ...prev, [currentLocation]: locationData };
      });

      // Deduct only delta cost already computed
      setMoney(prev => prev - displayCost);

      setShowFloorDesigner(false);
      if (droppedWithServers > 0) {
        triggerNotification(`${droppedWithServers} rack(s) outside 18×8 were removed from the design.`, 'warning');
      }
      triggerNotification(`Floor "${floorName}" updated successfully!`, 'success');
      return;
    }

    // Create a new floor with the design
    // Generate a unique floor id/name if creating new and collision exists
    const baseId = floorName.toLowerCase().replace(/\s+/g, '-');
    let uniqueId = baseId;
    let uniqueName = floorName;
    const existingFloors = (locations[currentLocation]?.floors || {}) as Record<string, Floor>;
    if (!editMode) {
      let i = 2;
      while (existingFloors[uniqueId]) {
        uniqueId = `${baseId}-${i}`;
        uniqueName = `${floorName} (${i})`;
        i++;
      }
    }
    const newFloor: Floor = {
      id: uniqueId,
      name: uniqueName,
      racks: [],
      floorQualityId: 'basic',
      designGrid: grid, // Store the design
    };

    // Add racks based on grid positions
    grid.forEach((row, rowIndex) => {
      row.forEach((cell, colIndex) => {
        if (cell.type === 'rack') {
          newFloor.racks.push({
            id: Date.now() + Math.floor(Math.random() * 1000), // Unique ID
            type: null, // Empty slot by default
            x: colIndex,
            z: rowIndex,
            servers: [],
            maxCapacity: 10, // Default capacity placeholder
            floorQuality: 'basic', // Default floor quality
            effectiveCooling: 100, // Default cooling
            powerDraw: 0, // No servers yet, so no power draw
            coolingNeeded: 0 // No servers yet, so no cooling needed
          });
        }
      });
    });

    // Update locations state
    setLocations(prev => {
      const locationData = {...prev[currentLocation]};
      if (!locationData.floors) locationData.floors = {};

      locationData.floors[uniqueId] = newFloor;

      // If invoked from a room in Site Layout, assign the floor to that room
      if (assignRoomId) {
        const nextRooms = { ...(locationData.rooms || {}) } as Record<string, any>;
        const room = nextRooms[assignRoomId];
        if (room) {
          nextRooms[assignRoomId] = { ...room, floorId: uniqueId };
          locationData.rooms = nextRooms as any;
        }
        const nextAssignments = { ...(locationData.roomAssignments || {}) } as Record<string, string>;
        nextAssignments[assignRoomId] = uniqueId;
        locationData.roomAssignments = nextAssignments;
      }

      // Recompute location-wide usage across all floors
      let totalServerPower = 0;
      let totalServerCooling = 0;
      Object.values(locationData.floors || {}).forEach(floor => {
        (floor.racks || []).forEach(rack => {
          (rack.servers || []).forEach(server => {
            const st = SERVER_TYPES.find(s => s.id === server.type);
            totalServerPower += st ? st.powerDraw : (server.powerDraw || 0);
            totalServerCooling += st ? st.coolingNeeded : (server.coolingNeeded || 0);
          });
        });
      });

      // Chillers: power consumption and cooling capacity
      let chillersPower = 0;
      let totalCooling = 0;
      (locationData.chillers || []).forEach(ch => {
        const type = CHILLER_TYPES.find(t => t.id === ch.typeId);
        const level = type?.levels.find(l => l.level === ch.level);
        if (level) {
          chillersPower += level.powerConsumptionKW;
          totalCooling += level.coolingOutputKW * level.efficiency;
        }
      });

      const usedPower = totalServerPower + chillersPower;
      const usedCooling = totalServerCooling;

      // Recalculate gross and available power
      const grossPower = calculateLocationGrossPower(locationData, POWER_GRIDS);
      locationData.grossPowerKW = grossPower;
      locationData.usedPower = usedPower;
      locationData.availablePowerKW = grossPower - usedPower;

      // Update cooling capacity/usage on location
      locationData.totalCoolingCapacityKW = totalCooling;
      locationData.usedCoolingCapacityKW = usedCooling;
      locationData.coolingCapacity = totalCooling;
      locationData.usedCooling = usedCooling;

      return {
        ...prev,
        [currentLocation]: locationData
      };
    });

    // Deduct cost
    setMoney(prev => prev - displayCost);

    // Close designer and notify
    setShowFloorDesigner(false);
    triggerNotification(`Floor "${uniqueName}" created successfully!`, 'success');
  };

  // Tool selection buttons
  const toolButtons = useMemo(() => [
    { type: 'rack', icon: <Grid className="w-5 h-5" />, label: 'Rack Space' },
    { type: 'cooling', icon: <Fan className="w-5 h-5" />, label: 'Cooling Unit' },
    { type: 'hot-aisle', icon: <Thermometer className="w-5 h-5 text-red-500" />, label: 'Hot Aisle' },
    { type: 'cold-aisle', icon: <Thermometer className="w-5 h-5 text-blue-500" />, label: 'Cold Aisle' },
    { type: 'power', icon: <div className="w-5 h-5 flex items-center justify-center">⚡</div>, label: 'Power Distribution' },
    { type: 'networking', icon: <div className="w-5 h-5 flex items-center justify-center">🌐</div>, label: 'Network' },
  ], []);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 rounded-lg shadow-xl max-w-7xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-700">
          <h2 className="text-xl font-bold text-white">{editMode ? 'Edit Floor Layout' : 'Floor Designer'} - {currentLocation}</h2>
          <button 
            onClick={() => setShowFloorDesigner(false)}
            className="text-gray-400 hover:text-white"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        {/* Main content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Tools sidebar */}
          <div className="w-64 bg-gray-800 p-4 flex flex-col">
            <h3 className="font-medium text-white mb-4">Tools</h3>
            
            {/* Tool buttons */}
            <div className="space-y-2 mb-6">
              {toolButtons.map(tool => (
                <button
                  key={tool.type}
                  onClick={() => setSelectedTool(tool.type as CellType)}
                  className={`flex items-center w-full p-2 rounded ${selectedTool === tool.type ? 'bg-blue-600' : 'bg-gray-700 hover:bg-gray-600'}`}
                >
                  {tool.icon}
                  <span className="ml-2 text-sm">{tool.label}</span>
                </button>
              ))}
            </div>
            
            {/* Floor name input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-1">Floor Name</label>
              <input
                type="text"
                value={floorName}
                onChange={(e) => setFloorName(e.target.value)}
                className="w-full bg-gray-700 text-white px-3 py-2 rounded border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
            </div>
            
            {/* Design templates */}
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-300 mb-2">Templates</h4>
              <button 
                onClick={applyFullRoom18x8}
                className="w-full bg-green-700 hover:bg-green-600 text-white py-2 px-3 rounded mb-2 text-sm"
              >
                18×8 Full Room (32 racks)
              </button>
              <button 
                onClick={applyHalfRoom18x8}
                className="w-full bg-emerald-700 hover:bg-emerald-600 text-white py-2 px-3 rounded text-sm"
              >
                18×8 Half Room (16 racks)
              </button>
            </div>
            
            <div className="flex-1"></div>
            
            {/* Cost estimate */}
            <div className="bg-gray-700 p-3 rounded mb-4">
              <div className="text-sm text-gray-300">{editMode ? 'Additional Cost' : 'Estimated Cost'}</div>
              <div className="text-xl font-bold text-green-400">{formatCurrency(displayCost)}</div>
              {money < displayCost && (
                <div className="text-xs text-red-400 mt-1">
                  You need {formatCurrency(displayCost - money)} more
                </div>
              )}
            </div>
            
            {/* Action buttons */}
            <button
              onClick={clearDesign}
              className="flex items-center justify-center bg-red-700 hover:bg-red-600 text-white py-2 px-4 rounded mb-2"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Clear Design
            </button>
            <button
              onClick={saveDesign}
              disabled={money < displayCost}
              className={`flex items-center justify-center ${money < displayCost ? 'bg-gray-600 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-500'} text-white py-2 px-4 rounded`}
            >
              <Save className="w-4 h-4 mr-2" /> {editMode ? 'Save Changes' : 'Save & Build'} ({formatCurrency(displayCost)})
            </button>
          </div>
          
          {/* Design grid */}
          <div className="flex-1 bg-gray-900 p-6 overflow-auto">
            <div className="bg-gray-800 p-1 rounded-lg inline-block">
              <div className="space-y-[1px]">
                {grid.map((row, rowIndex) => (
                  <EditorRow
                    key={`row-${rowIndex}`}
                    row={row}
                    rowIndex={rowIndex}
                    cols={GRID_COLS}
                    onCellClick={updateCell}
                    activeTool={selectedTool}
                  />
                ))}
              </div>
            </div>
            
            {/* Legend */}
            <div className="mt-6 bg-gray-800 p-4 rounded-lg">
              <h4 className="font-medium text-white mb-2">Design Guidelines</h4>
              <ul className="text-sm text-gray-300 space-y-1">
                <li>• Walkways (empty) around hot/cold aisles improve access</li>
                <li>• Cold aisles should be placed between rows of racks facing each other</li>
                <li>• Hot aisles should be placed behind racks to collect exhaust heat</li>
                <li>• Place cooling units near hot aisles for maximum efficiency</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FloorDesigner;
